import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Cloud, Database, FolderOpen, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import CloudStorageConnectorDialog from '../CloudStorageConnectorDialog';

// The order sources appear in Plugins and the Knowledge Hub.
export const SOURCE_ORDER = ['sharepoint', 'onedrive', 'google_drive', 'amazon_s3', 'azure_blob', 'dropbox', 'box', 'confluence'];
const SOURCE_ICONS = { amazon_s3: Database, azure_blob: Database };
export const SourceIcon = ({ resource, size = 17 }) => {
  const Icon = SOURCE_ICONS[resource] || Cloud;
  return <span className="qflow-home-icon grid h-9 w-9 shrink-0 place-items-center"><Icon size={size} aria-hidden="true" /></span>;
};

const timeAgo = (value) => {
  if (!value) return 'not synced yet';
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 60) return `synced ${Math.max(minutes, 1)}m ago`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `synced ${hours}h ago` : `synced ${new Date(value).toLocaleDateString()}`;
};

export const useKnowledgeSources = () => {
  const [status, setStatus] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/cloud-connectors/status');
      setStatus(data);
    } catch (_) {
      setStatus(null);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const sources = SOURCE_ORDER
    .filter((resource) => status?.resources?.[resource])
    .map((resource) => ({ resource, ...status.resources[resource] }));
  return { status, sources, reload: load };
};

const sourceState = (source) => {
  if (source.connected && source.locations) return ['Connected', 'qflow-home-pill-good'];
  if (source.connected) return ['Choose folders', 'qflow-home-pill-warn'];
  if (source.available) return null;
  return ['Soon', 'qflow-home-chip'];
};

// Plugins → Knowledge sources: where an organisation connects its storage.
const flattenTeams = (tree = []) => tree.flatMap((sector) => (sector.departments || []).flatMap((department) => department.teams || []));

export const PluginsKnowledgeSources = ({ canConnect }) => {
  const { sources, reload } = useKnowledgeSources();
  const [open, setOpen] = useState(null);
  const [teams, setTeams] = useState([]);
  useEffect(() => {
    axiosInstance.get('/categories/tree').then(({ data }) => setTeams(flattenTeams(data?.tree))).catch(() => setTeams([]));
  }, []);
  return (
    <section id="knowledge-sources" className="qflow-home-card p-5" data-testid="plugins-knowledge-sources">
      <h2 className="text-base font-bold">Knowledge sources</h2>
      <p className="mt-0.5 text-sm text-gray-500">Bring documents from where your organisation already keeps them. Qflow only reads; it never changes or deletes your files.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {sources.map((source) => {
          const state = sourceState(source);
          const clickable = (source.available || source.connected) && canConnect;
          return (
            <button key={source.resource} type="button" disabled={!clickable} onClick={() => setOpen(source.resource)} className="qflow-home-card qflow-home-tile flex items-center gap-3 p-3 text-left disabled:cursor-default" data-testid={`source-card-${source.resource}`}>
              <SourceIcon resource={source.resource} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">{source.label}</span>
                <span className="block truncate text-xs text-gray-500">{source.connected ? `${source.locations} location${source.locations === 1 ? '' : 's'} · ${source.files} files` : source.auth === 'credentials' ? 'Paste read-only access from IT' : source.description}</span>
              </span>
              {state ? <span className={`qflow-home-pill ${state[1]}`}>{state[0]}</span> : <span className="qflow-accent-bg rounded-lg px-3 py-1 text-xs font-semibold">Connect</span>}
            </button>
          );
        })}
      </div>
      <CloudStorageConnectorDialog canConnect={canConnect} open={Boolean(open)} onOpenChange={(next) => { if (!next) { setOpen(null); reload(); } }} resource={open || 'sharepoint'} teams={teams} />
    </section>
  );
};

// Knowledge Hub → Your sources: shortcuts to connected sources people chose to show here.
export const HubSourceShortcuts = ({ teams = [], canManage, canConnect, onImported }) => {
  const navigate = useNavigate();
  const { sources, reload } = useKnowledgeSources();
  const [open, setOpen] = useState(null);
  const [syncing, setSyncing] = useState('');
  const shortcuts = sources.filter((source) => source.in_hub);
  if (!shortcuts.length && !canConnect) return null;

  const syncNow = async (source) => {
    setSyncing(source.resource);
    try {
      const { data } = await axiosInstance.post(`/cloud-connectors/${source.provider}/sync`);
      toast.success(`${source.label}: ${data.imported || 0} new, ${data.unchanged || 0} unchanged, ${data.change_pending_review || 0} to review${data.failed ? `, ${data.failed} couldn’t be read` : ''}`);
      onImported?.();
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not sync ${source.label}`);
    } finally {
      setSyncing('');
    }
  };

  return (
    <section className="mb-4" data-testid="hub-source-shortcuts">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Your sources</h3>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {shortcuts.map((source) => (
          <div key={source.resource} className="qflow-home-card p-3" data-testid={`hub-source-${source.resource}`}>
            <div className="flex items-center gap-3">
              <SourceIcon resource={source.resource} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{source.label}</p>
                <p className="flex items-center gap-1 truncate text-xs text-gray-500">{source.last_sync_failed ? <><AlertTriangle size={12} className="text-amber-500" aria-hidden="true" />{source.last_sync_failed} couldn’t be read</> : `${source.files} files · ${timeAgo(source.last_sync_at)}`}</p>
              </div>
            </div>
            {canManage && <div className="mt-3 flex gap-2">
              <button type="button" onClick={() => setOpen(source.resource)} className="qflow-home-chip flex items-center gap-1 px-3 py-1 text-xs font-semibold"><FolderOpen size={13} aria-hidden="true" />Open</button>
              <button type="button" onClick={() => syncNow(source)} disabled={syncing === source.resource} className="qflow-home-chip flex items-center gap-1 px-3 py-1 text-xs font-semibold" data-testid={`hub-source-sync-${source.resource}`}><RefreshCw size={13} className={syncing === source.resource ? 'animate-spin' : ''} aria-hidden="true" />Sync now</button>
            </div>}
          </div>
        ))}
        {canConnect && <button type="button" onClick={() => navigate('/integrations#knowledge-sources')} className="qflow-home-card grid place-items-center border-dashed p-3 text-center" style={{ borderStyle: 'dashed' }} data-testid="hub-connect-source">
          <span><Plus size={16} className="qflow-accent-text mx-auto" aria-hidden="true" /><span className="qflow-accent-text block text-sm font-semibold">Connect a source</span><span className="block text-xs text-gray-500">SharePoint, Drive, S3 and more, in Plugins</span></span>
        </button>}
      </div>
      <CloudStorageConnectorDialog canConnect={canConnect} open={Boolean(open)} onOpenChange={(next) => { if (!next) { setOpen(null); reload(); } }} resource={open || 'sharepoint'} teams={teams} onImported={onImported} />
    </section>
  );
};
